/**
 * The policies this person owns that are due, or overdue, for review.
 *
 * The owner's half of the review cycle. The reminder email says the same
 * thing once a milestone passes; this says it every time they open the app.
 */

import { useQuery } from '@tanstack/react-query';
import { call } from '../../lib/callable';

export interface ReviewDue {
  id: string;
  policyNumber: string | null;
  title: string;
  reviewStatus: 'due_soon' | 'overdue';
  /** ISO date. */
  nextReviewAt: string | null;
}

interface Raw {
  policies?: Array<Record<string, unknown>>;
  count?: number;
}

function toReview(r: Record<string, unknown>): ReviewDue | null {
  const id = typeof r.id === 'string' ? r.id : null;
  if (!id) return null;
  return {
    id,
    policyNumber: typeof r.policyNumber === 'string' && r.policyNumber ? r.policyNumber : null,
    title: typeof r.title === 'string' ? r.title : '(untitled policy)',
    reviewStatus: r.reviewStatus === 'overdue' ? 'overdue' : 'due_soon',
    nextReviewAt: typeof r.nextReviewAt === 'string' ? r.nextReviewAt.slice(0, 10) : null,
  };
}

export function useMyReviews(enabled: boolean) {
  return useQuery({
    queryKey: ['my-reviews'],
    enabled,
    queryFn: async (): Promise<ReviewDue[]> => {
      const res = await call<Record<string, never>, Raw>('getMyPoliciesNeedingReview', {});
      return (res.policies ?? []).map(toReview).filter((p): p is ReviewDue => p !== null);
    },
  });
}
